import * as React from 'react';
import { Dropdown, Text, Icon, TextField, MessageBar, MessageBarType, DefaultButton, PrimaryButton, Spinner, SpinnerSize, Dialog, DialogType, DialogFooter } from '@fluentui/react';
import type { IDropdownOption } from '@fluentui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';

import type { IPBTCase, IUpdatePbtCaseRequest } from '../../../Interfaces/IPBTCase';
import { PBTOutcomeOptions, PBTRehireOptions, PBTWorkflowSteps, PBTCaseDetailTabs, PBTApiEndpoints, PBTMessages, PBTLookupCategories, humanizeReminderMessage } from '../../../Services/PBTConstants';
import { useProceedHrcValidation } from '../../../Services/PBT';
import { usePbtLookupOptions } from '../../../Services/PBT/usePbtLookupOptions';
import { PBTEngagementModel, PBTCaseStatus, PBTFlag, PBTDataStatus, normalizePbtFlags } from '../../../Interfaces/IPBTCase';
import { isHrcLedBypass, getManagerActionActor } from '../../../Services/PBT/hrcBypassUtils';
import { useBypassManager } from '../../../Services/PBT/useBypassManager';
import { PauseCaseDialog, CancelCaseDialog, ResumeCaseDialog } from '../CaseActionDialogs';
import { useCaseActions } from '../CaseActionDialogs/useCaseActions';
import { caseDetailStyles } from './CaseDetail.styled';
import { EmployeeDataTab } from './EmployeeDataTab';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { AuditLog } from '../AuditLog/AuditLog';
import { AttestationPanel } from '../AttestationPanel/AttestationPanel';
import { updateCaseRequest } from '../../../Redux/PBT/Cases/actions';
import type { IPBTCasesState } from '../../../Redux/PBT/Cases/types';
import { TermDateDialog } from './TermDateDialog';
import { updateCaseInStore } from '../../../Pages/PBT/HRCDashboard/useHRCDashboard';
import { usePermission, useFeature, useRole } from '../../../Hooks/useFeature';

interface CaseDetailProps {
  selectedCase: IPBTCase;
  onClose: () => void;
  onCaseUpdated?: () => void;
  // Story 5.3: fired after the HRC validation gate is released. Distinct from onCaseUpdated so the
  // dashboard can simply re-fetch without optimistically mutating the engagement model.
  onCaseReleased?: () => void;
}

const WORKFLOW_STEPS = PBTWorkflowSteps;
const TABS = PBTCaseDetailTabs;

// Index of the "Workflow Actions" tab within PBTCaseDetailTabs.
const WORKFLOW_ACTIONS_TAB_INDEX = TABS.indexOf('Workflow Actions');

// Cases that have reached the attestation milestone open directly on the Workflow Actions
// tab (where the HRC takes the next action) rather than the default Case Details tab.
const getDefaultTabIndex = (status: string): number =>
  status === PBTCaseStatus.AwaitingAttestation && WORKFLOW_ACTIONS_TAB_INDEX >= 0
    ? WORKFLOW_ACTIONS_TAB_INDEX
    : 0;

const fallbackOutcomeOptions: IDropdownOption[] = PBTOutcomeOptions.map((o) => ({ key: o.key, text: o.text }));
const fallbackRehireOptions: IDropdownOption[] = PBTRehireOptions.map((o) => ({ key: o.key, text: o.text }));
const engModelOptions: IDropdownOption[] = Object.values(PBTEngagementModel).map((v) => ({ key: v, text: v }));

export const CaseDetail: React.FC<CaseDetailProps> = ({ selectedCase, onClose, onCaseUpdated, onCaseReleased }) => {
  const dispatch = useDispatch();
  const casesState = useSelector(
    (state: { dynamic?: Record<string, unknown> }) =>
      (state?.dynamic?.['pbtCasesReducer']) as IPBTCasesState | undefined
  );
  const { isUpdating = false, updateError = null } = casesState || {};
  const isManager = useRole() === 'Manager';
  // Only an Admin sees and acts on the HRC validation gate / Proceed step. Managers and HRCs do
  // not see the HRC Approval step at all (they still attest as before).
  const isAdmin = useRole() === 'Admin';
  const [activeTab, setActiveTab] = React.useState(() => getDefaultTabIndex(selectedCase.status));
  const { options: outcomeOptions } = usePbtLookupOptions(PBTLookupCategories.CrmOutcome, fallbackOutcomeOptions);
  const { options: rehireOptions } = usePbtLookupOptions(PBTLookupCategories.RehireEligibility, fallbackRehireOptions);
  const [outcome, setOutcome] = React.useState(selectedCase.outcome ?? '');
  const [rehire, setRehire] = React.useState(selectedCase.rehire ?? '');
  const [engModel, setEngModel] = React.useState<string>(selectedCase.engModel ?? '');
  const [backupHrcAlias, setBackupHrcAlias] = React.useState(selectedCase.backupHrc ?? '');
  const [assignedHrc, setAssignedHrc] = React.useState(selectedCase.hrc ?? '');
  const [displayedHrcName, setDisplayedHrcName] = React.useState(selectedCase.hrcName ?? '—');
  
  // Track last saved values to prevent false positives in change detection
  const [lastSavedOutcome, setLastSavedOutcome] = React.useState(selectedCase.outcome ?? '');
  const [lastSavedRehire, setLastSavedRehire] = React.useState(selectedCase.rehire ?? '');
  const [lastSavedEngModel, setLastSavedEngModel] = React.useState<PBTEngagementModel | (string & NonNullable<unknown>) | ''>(selectedCase.engModel);
  const [lastSavedBackupHrc, setLastSavedBackupHrc] = React.useState(selectedCase.backupHrc ?? '');
  const [lastSavedAssignedHrc, setLastSavedAssignedHrc] = React.useState(selectedCase.hrc ?? '');
  
  const [confirmDialogVisible, setConfirmDialogVisible] = React.useState(false);
  const [confirmImpacts, setConfirmImpacts] = React.useState<string[]>([]);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [hasPendingAttestationSuccess, setHasPendingAttestationSuccess] = React.useState(false);

  // HRC Bypass state and hooks
  const [showBypassDialog, setShowBypassDialog] = React.useState(false);
  const isBypassed = isHrcLedBypass(selectedCase);
  const actionActor = getManagerActionActor(selectedCase);
  const { bypassManager, isSubmitting, error: bypassError } = useBypassManager();

  // Send Reminder state
  const { authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  const [isSendingReminder, setIsSendingReminder] = React.useState(false);
  const [reminderMessage, setReminderMessage] = React.useState<{ type: MessageBarType; text: string } | null>(null);
  const [localReminderCount, setLocalReminderCount] = React.useState((selectedCase.reminderCount ?? 0) + (selectedCase.manualReminderCount ?? 0));
  const [localLastSentAt, setLocalLastSentAt] = React.useState(selectedCase.lastReminderSentAt ?? null);

  // Re-send termination email (P0-3 / US 15949981) state
  const [isRetryingEmail, setIsRetryingEmail] = React.useState(false);
  const [retryEmailMessage, setRetryEmailMessage] = React.useState<{ type: MessageBarType; text: string } | null>(null);
  const [emailFailedCleared, setEmailFailedCleared] = React.useState(false);

  // Reset reminder state when switching cases
  React.useEffect(() => {
    setLocalReminderCount((selectedCase.reminderCount ?? 0) + (selectedCase.manualReminderCount ?? 0));
    setLocalLastSentAt(selectedCase.lastReminderSentAt ?? null);
    setReminderMessage(null);
    setHasPendingAttestationSuccess(false);
    setRetryEmailMessage(null);
    setEmailFailedCleared(false);
  }, [selectedCase.id]);

  // If the same case refreshes in-place and (still) reports a delivery failure, re-show the banner:
  // an optimistic clear from a prior retry must not permanently hide a genuine, current failure.
  // We key off the server's delivery signals (not just the emailFailed boolean) so that a fresh
  // failure surfaced by a same-value refresh (emailFailed stays true → true, but a new send attempt
  // updates termEmailSentDate / emailFailedDetail) still resets the optimistic clear.
  React.useEffect(() => {
    if (selectedCase.emailFailed) {
      setEmailFailedCleared(false);
    }
  }, [selectedCase.emailFailed, selectedCase.termEmailSentDate, selectedCase.emailFailedDetail]);

  const canSendReminder = usePermission('Cases.Edit') && selectedCase.termConvDate == null && !selectedCase.managerNonResponsive && selectedCase.kickoffEmailSent === true;

  const handleSendReminder = React.useCallback(async () => {
    setIsSendingReminder(true);
    setReminderMessage(null);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.SendReminder(selectedCase.id), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const result = await response.json();
      if (result.remindersSent > 0) {
        setReminderMessage({ type: MessageBarType.success, text: PBTMessages.ReminderSendSuccess });
        setLocalReminderCount((prev) => prev + 1);
        setLocalLastSentAt(new Date().toISOString());
      } else {
        setReminderMessage({ type: MessageBarType.warning, text: humanizeReminderMessage(result.messages?.[0], selectedCase.empName) });
      }
    } catch {
      setReminderMessage({ type: MessageBarType.error, text: PBTMessages.ReminderSendError });
    } finally {
      setIsSendingReminder(false);
    }
  }, [selectedCase.id, selectedCase.empName, authClient]);

  const handleRetryTermEmail = React.useCallback(async () => {
    setIsRetryingEmail(true);
    setRetryEmailMessage(null);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.RetryTermEmail(selectedCase.id), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const result = await response.json();
      // The API responds with an OkPm envelope: the TermEmailResult is serialized under `data`.
      // Fall back to the unwrapped shape so both response formats are handled safely.
      const payload = result?.data ?? result;
      const recipientResults: Array<{ success?: boolean }> = Array.isArray(payload?.recipientResults) ? payload.recipientResults : [];
      const total = recipientResults.length;
      const successCount = recipientResults.filter((r) => r?.success === true).length;
      const failureCount = total - successCount;
      if (total === 0) {
        setRetryEmailMessage({ type: MessageBarType.warning, text: PBTMessages.TermEmailRetryNothing });
      } else if (failureCount === 0) {
        setRetryEmailMessage({ type: MessageBarType.success, text: PBTMessages.TermEmailRetrySuccess });
        // Only clear the delivery-failure state when EVERY recipient was re-sent successfully. A mixed
        // response still has a failed recipient, so the banner must remain.
        setEmailFailedCleared(true);
      } else if (successCount === 0) {
        setRetryEmailMessage({ type: MessageBarType.error, text: PBTMessages.TermEmailRetryAllFailed });
      } else {
        setRetryEmailMessage({ type: MessageBarType.warning, text: PBTMessages.TermEmailRetryPartial(successCount, failureCount) });
      }
    } catch {
      setRetryEmailMessage({ type: MessageBarType.error, text: PBTMessages.TermEmailRetryError });
    } finally {
      setIsRetryingEmail(false);
    }
  }, [selectedCase.id, authClient]);

  // Helper function to format dates
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Check if any changes have been made (compare against last saved values)
  const hasChanges = React.useMemo(() => {
    return outcome !== lastSavedOutcome ||
           rehire !== lastSavedRehire ||
           engModel !== lastSavedEngModel ||
           backupHrcAlias !== lastSavedBackupHrc ||
           assignedHrc !== lastSavedAssignedHrc;
  }, [outcome, rehire, engModel, backupHrcAlias, assignedHrc, lastSavedOutcome, lastSavedRehire, lastSavedEngModel, lastSavedBackupHrc, lastSavedAssignedHrc]);

  const {
    pauseDialogOpen, cancelDialogOpen, resumeDialogOpen, targetCase, updatedCase, actionResult,
    openPauseDialog, openCancelDialog, openResumeDialog, closePauseDialog, closeCancelDialog, closeResumeDialog,
    handlePauseConfirm, handleResumeConfirm, handleCancelConfirm, clearActionResult, clearUpdatedCase,
  } = useCaseActions();

  // After a successful pause/resume/cancel the API returns the updated case;
  // reflect it locally so the status badge and available actions update
  // immediately instead of showing stale data until the next prop refresh.
  const effectiveCase = updatedCase ?? selectedCase;

  // Check if case is locked (term email sent OR status is Paused/Complete/Cancelled).
  // Derived from effectiveCase so an in-session pause/resume/cancel locks/unlocks the
  // Case Details fields immediately, consistent with the status badge and action buttons,
  // instead of waiting for the parent prop refresh.
  const isLocked = !!effectiveCase.termEmailSentDate ||
                   effectiveCase.status === PBTCaseStatus.Paused ||
                   effectiveCase.status === PBTCaseStatus.Complete ||
                   effectiveCase.status === PBTCaseStatus.Cancelled;

  const pausedFlagLabel = React.useMemo(() => {
    if (isManager || effectiveCase.status !== PBTCaseStatus.Paused) return '';

    const flags: string[] = [];
    const complianceFlags = normalizePbtFlags(effectiveCase.flags);
    if (
      !effectiveCase.hrcGerWitReviewed
      && (complianceFlags.includes(PBTFlag.GERWIT) || effectiveCase.gerWitCheckStatus)
    ) {
      flags.push(PBTFlag.GERWIT);
    }
    if (!effectiveCase.hrcLoaReviewed && complianceFlags.includes(PBTFlag.LOA)) {
      flags.push(PBTFlag.LOA);
    }
    if (effectiveCase.isManagerChanged && !effectiveCase.isManagerChangeReviewed) {
      flags.push('MGR');
    }
    return flags.join(' / ');
  }, [
    effectiveCase.flags,
    effectiveCase.gerWitCheckStatus,
    effectiveCase.hrcGerWitReviewed,
    effectiveCase.hrcLoaReviewed,
    effectiveCase.isManagerChanged,
    effectiveCase.isManagerChangeReviewed,
    effectiveCase.status,
    isManager,
  ]);

  // Determine lock reason for banner message
  const lockMessage = React.useMemo(() => {
    if (effectiveCase.termEmailSentDate) {
      return `Case is locked — the termination email was sent on ${formatDate(effectiveCase.termEmailSentDate)}`;
    }
    switch (effectiveCase.status) {
      case PBTCaseStatus.Paused:
        return pausedFlagLabel
          ? `Case is locked because of ${pausedFlagLabel}`
          : 'Case is locked — the case is paused';
      case PBTCaseStatus.Complete: return 'Case is locked — the case is complete';
      case PBTCaseStatus.Cancelled: return 'Case is locked — the case is cancelled';
      default: return '';
    }
  }, [effectiveCase.termEmailSentDate, effectiveCase.status, pausedFlagLabel]);

  // Bug 16442069 / 16442169: when a case is blocked on required Employee Data, the downstream
  // workflow tabs (Workflow Actions, Documents, Audit Log) must be disabled and the Data Gathering
  // stepper node must render an error (X) instead of a completion check. This blocked state must be
  // known the moment the case is opened — irrespective of the active tab — so we fetch the employee
  // data field-completeness at the case level on open. `empDataBlocked` (reported by the Employee
  // Data tab once mounted) takes precedence so live retries/overrides update the UI immediately.
  const [empDataBlocked, setEmpDataBlocked] = React.useState<boolean | null>(null);
  const [caseLevelBlocked, setCaseLevelBlocked] = React.useState<boolean | null>(null);
  // Tracks the in-flight case-level completeness lookup. Downstream tabs stay disabled while this is
  // true so a blocked case can't expose Workflow Actions during the fetch window (when dataStatus is
  // not authoritative). Set false in BOTH success and failure paths so a failed lookup settles
  // rather than locking the tabs permanently.
  const [empDataLookupPending, setEmpDataLookupPending] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    const pern = selectedCase.pern;
    if (!pern) {
      setCaseLevelBlocked(null);
      setEmpDataLookupPending(false);
      return;
    }
    setEmpDataLookupPending(true);
    (async () => {
      try {
        const token = await authClient.acquireToken(__SCOPES__);
        const res = await fetch(PBTApiEndpoints.EmployeeData(pern, selectedCase.id), {
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        const json = await res.json();
        const fc = (json?.data?.fieldCompleteness ?? {}) as Record<string, string>;
        // Mirror the Employee Data tab (bug 16452564): PersonalEmail is excluded from
        // data-completeness and never contributes to the blocked state, even if the backend still
        // reports it as Blocking. Keeping this in sync avoids a case reading as blocked here while the
        // Employee Data tab (and its Retry/Override banner) shows no blocking fields.
        const blocked = Object.entries(fc).some(([field, status]) => field !== 'PersonalEmail' && status === 'Blocking');
        if (!cancelled) setCaseLevelBlocked(blocked);
      } catch {
        if (!cancelled) setCaseLevelBlocked(null);
      } finally {
        if (!cancelled) setEmpDataLookupPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCase.pern, authClient]);

  // Authoritative "blocked on required data" signal. Prefer the live signals (the Employee Data tab's
  // report, then the case-level completeness lookup). Only fall back to the known-unreliable
  // effectiveCase.dataStatus string once the completeness lookup has SETTLED (not while it is still
  // pending): otherwise a freshly-promoted case whose grid row still carries a stale
  // dataStatus === 'Blocked' would briefly flash the stepper error X and the workflow-blocked banner
  // for the ~2s the lookup is in flight, before the live result clears them.
  const isBlockedOnData =
    empDataBlocked !== null
      ? empDataBlocked
      : caseLevelBlocked !== null
      ? caseLevelBlocked
      : empDataLookupPending
      ? false
      : effectiveCase.dataStatus === PBTDataStatus.Blocked;

  // Downstream tabs are locked when the case is known to be blocked OR while the completeness lookup
  // is still resolving — the latter closes the race where dataStatus (unreliable) would briefly
  // enable Workflow Actions on a case that turns out to be blocked. The stepper X, by contrast, is
  // driven only by the definitive `isBlockedOnData` so healthy cases never flash an error icon.
  const isDownstreamLocked = isBlockedOnData || empDataLookupPending;

  // Refresh the dashboard grid once whenever a pause/resume/cancel produces a new case. The
  // callback is read from a ref so an unstable parent handler can't retrigger the effect (which
  // would loop through refreshCases). Uses the plain refresh path so a status change never mutates
  // the engagement model.
  const refreshOnCaseChangeRef = React.useRef(onCaseReleased);
  refreshOnCaseChangeRef.current = onCaseReleased;
  const notifiedUpdatedCaseRef = React.useRef<IPBTCase | null>(null);
  React.useEffect(() => {
    if (updatedCase && notifiedUpdatedCaseRef.current !== updatedCase) {
      notifiedUpdatedCaseRef.current = updatedCase;
      refreshOnCaseChangeRef.current?.();
    }
  }, [updatedCase]);

  // ── Story 5.3: HRC Validation Gate "Proceed" action ──
  const isAwaitingHrcApproval = effectiveCase.status === PBTCaseStatus.AwaitingHrcApproval;
  // A completed case that carries a release stamp went through the HRC validation gate, so we
  // keep the "HRC Approval" step visible (as a completed milestone) on its workflow.
  const isReleasedComplete =
    effectiveCase.status === PBTCaseStatus.Complete && !!effectiveCase.hrcValidationReleasedBy;
  // Admin-only: the HRC Approval milestone and Proceed action are visible only to Admins.
  const showHrcApprovalStep = isAdmin && (isAwaitingHrcApproval || isReleasedComplete);
  const [confirmVisible, setConfirmVisible] = React.useState(false);
  const {
    proceed,
    isSubmitting: proceeding,
    isProcessing: proceedProcessing,
    state: proceedState,
    error: proceedError,
    reset: resetProceed,
  } = useProceedHrcValidation(onCaseReleased);
  const proceedDone = proceedState === 'Completed';

  const blockingFlags = React.useMemo(() => {
    const flags: string[] = normalizePbtFlags(effectiveCase.flags).filter((flag) => {
      if (flag === PBTFlag.GERWIT) return !effectiveCase.hrcGerWitReviewed;
      if (flag === PBTFlag.LOA) return !effectiveCase.hrcLoaReviewed;
      return false;
    });
    if (effectiveCase.isManagerChanged && !effectiveCase.isManagerChangeReviewed) {
      flags.push('MGR');
    }
    return flags;
  }, [
    effectiveCase.flags,
    effectiveCase.hrcGerWitReviewed,
    effectiveCase.hrcLoaReviewed,
    effectiveCase.isManagerChanged,
    effectiveCase.isManagerChangeReviewed,
  ]);
  const hasBlockingFlag = blockingFlags.length > 0;
  // Bug 16434325: the workflow is halted while the case is in an active state and EITHER a compliance
  // flag (GERWIT / LOA) is set OR required employee data is missing (server-computed
  // dataStatus === 'Blocked'). Both feed this single gate, so every progression / document action
  // guarded by isWorkflowBlocked is disabled until the block is cleared (Retry Pull / Manual Override).
  const isActiveStatus =
    effectiveCase.status !== PBTCaseStatus.Paused &&
    effectiveCase.status !== PBTCaseStatus.Complete &&
    effectiveCase.status !== PBTCaseStatus.Cancelled;
  const isFlagBlocked = hasBlockingFlag && isActiveStatus;
  // Use the authoritative live completeness signal (isBlockedOnData) rather than the raw, and
  // known-unreliable, effectiveCase.dataStatus === 'Blocked' string. Otherwise the top banner and the
  // attestation/Proceed gate keep reporting the case as blocked after a Retry Pull / Manual Override
  // has actually cleared the required fields, until the grid row's stale dataStatus is refreshed.
  const isDataBlocked = isBlockedOnData && isActiveStatus;
  const isWorkflowBlocked = isFlagBlocked || isDataBlocked;
  const blockingFlagLabel = blockingFlags.join(' / ');
  const workflowBlockedMessage = isFlagBlocked
    ? isManager
      ? PBTMessages.ManagerFlaggedComplexity
      : `Case is locked because of ${blockingFlagLabel}. This case requires HRC review before the workflow can continue.`
    : isManager
    ? 'Workflow paused. This case requires HRC review before the workflow can continue.'
    : 'Workflow blocked: required employee data is missing. Open the Employee Data tab and use Retry Pull from Source or Enter Manual Override to supply the missing fields before the workflow can continue.';

  // Term-date selection is intentionally NOT blocked by a GER/WIT flag (per requirement). LOA,
  // an unreviewed manager change, and employee-data readiness still gate it.
  const isLoaBlocked =
    normalizePbtFlags(effectiveCase.flags).includes(PBTFlag.LOA) &&
    !effectiveCase.hrcLoaReviewed &&
    isActiveStatus;
  const isManagerChangeBlocked =
    effectiveCase.isManagerChanged === true &&
    effectiveCase.isManagerChangeReviewed !== true &&
    isActiveStatus;
  // Use the authoritative employee-data block signal (isDownstreamLocked = isBlockedOnData ||
  // empDataLookupPending) rather than the raw dataStatus === 'Blocked' string, so term-date
  // selection is gated by the same definitive check that disables the Workflow Actions tab.
  const isTermDateSelectionBlocked =
    isLoaBlocked || isManagerChangeBlocked || isDownstreamLocked;
  const reviewedIndicators = React.useMemo(() => {
    const indicators: string[] = [];
    if (effectiveCase.hrcLoaReviewed && effectiveCase.flags?.includes(PBTFlag.LOA)) {
      indicators.push(PBTFlag.LOA);
    }
    if (effectiveCase.hrcGerWitReviewed && effectiveCase.flags?.includes(PBTFlag.GERWIT)) {
      indicators.push(PBTFlag.GERWIT);
    }
    if (effectiveCase.isManagerChanged && effectiveCase.isManagerChangeReviewed) indicators.push('MGR');
    return indicators;
  }, [
    effectiveCase.flags,
    effectiveCase.hrcGerWitReviewed,
    effectiveCase.hrcLoaReviewed,
    effectiveCase.isManagerChanged,
    effectiveCase.isManagerChangeReviewed,
  ]);

  React.useEffect(() => {
    resetProceed();
    setConfirmVisible(false);
  }, [selectedCase.id, resetProceed]);

  const onProceed = React.useCallback(async () => {
    if (isWorkflowBlocked) return;
    await proceed(effectiveCase.id);
    setConfirmVisible(false);
  }, [proceed, effectiveCase.id, isWorkflowBlocked]);

  // Story 5.3: surface a dedicated "HRC Approval" step (inserted between Date & Attestation Pending and
  // Complete) when the case is at the gate (AwaitingHrcApproval) or has been released and is
  // now complete. Driven by case data only — no separate feature-flag lookup is needed.
  const steps = React.useMemo<string[]>(() => {
    if (!showHrcApprovalStep) return [...WORKFLOW_STEPS];
    const attestIdx = WORKFLOW_STEPS.indexOf(PBTCaseStatus.AwaitingAttestation);
    return [
      ...WORKFLOW_STEPS.slice(0, attestIdx + 1),
      'HRC Approval',
      ...WORKFLOW_STEPS.slice(attestIdx + 1),
    ];
  }, [showHrcApprovalStep]);

  const [isTermDateDialogOpen, setIsTermDateDialogOpen] = React.useState(false);
  const [termConvDate, setTermConvDate] = React.useState(selectedCase.termConvDate);

  // Derive currentStep from case status using the canonical step numbers (mirrors the base
  // switch). The HRC Approval step is inserted at position 6 when present, so Complete shifts to
  // 7 on a gated/released case and stays 6 otherwise.
  const currentStep = React.useMemo(() => {
    switch (effectiveCase.status) {
      case PBTCaseStatus.Pending: return 1;
      case PBTCaseStatus.DataGathering: return 3;
      case PBTCaseStatus.AwaitingAttestation: return 4;
      case PBTCaseStatus.AwaitingHrcApproval: return 5;
      case PBTCaseStatus.Complete: return showHrcApprovalStep ? 7 : 6;
      default: return effectiveCase.currentStep ?? 1;
    }
  }, [effectiveCase.status, effectiveCase.currentStep, showHrcApprovalStep]);

  // Task 16525699: the first three workflow steps (CRM Sync, Details Entry, Data Gathering) are
  // HRC-internal intake steps. Managers should only see the workflow from "Date & Attestation
  // Pending" onward. These display-only projections hide those steps and offset the highlighted
  // node so "current step" and "Step X of Y" stay aligned. The canonical `currentStep` above is
  // intentionally left untouched so every business/logic gate (e.g. bypass eligibility) is
  // unaffected. HRC continues to see the full, unchanged list. The Math.max clamp guards the rare
  // case of a manager opening a pre-attestation case so the index never goes to 0 or negative.
  const MANAGER_HIDDEN_STEP_COUNT = 3;
  const displaySteps = isManager ? steps.slice(MANAGER_HIDDEN_STEP_COUNT) : steps;
  const displayCurrentStep = isManager
    ? Math.max(1, currentStep - MANAGER_HIDDEN_STEP_COUNT)
    : currentStep;

  // Role permissions (from feature-config `permissions[]`, matching the API [AuthorizedRole] gates).
  const canEditPerm = usePermission('Cases.Edit');
  const canPausePerm = usePermission('Cases.Pause');
  const canCancelPerm = usePermission('Cases.Cancel');
  const canProceedPerm = usePermission('Validation.Proceed');

  // Workflow-tab buttons gated by the UI feature config (not permissions).
  // accessLevel 0 = hidden, 1 = visible-but-disabled (read-only), 2 = enabled.
  const WORKFLOW_FEATURE_PATH = 'page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow';
  const setTermDateFeature = useFeature(`${WORKFLOW_FEATURE_PATH}/feature.workflow.setTermDate`);
  const bypassFeature = useFeature(`${WORKFLOW_FEATURE_PATH}/feature.workflow.bypass`);
  // Informational workflow panels, gated as Sections. Hidden for the Manager "My Cases" role
  // (unmapped) but enabled for HRC/HRCLeader/Admin.
  const kickoffEmailFeature = useFeature(`${WORKFLOW_FEATURE_PATH}/section.workflow.kickoffEmail`);
  const remindersFeature = useFeature(`${WORKFLOW_FEATURE_PATH}/section.workflow.reminders`);

  // Case-detail tabs are gated by the UI feature config so each role only sees the tabs it may
  // use. HRC/HRCLeader/Admin have all tabs at level 2 (unchanged); the Manager "My Cases" role
  // only has tab.caseDetail.workflow enabled, so they see just the Workflow Actions tab and, in
  // it, only the Set/Change Term Date action. Tab index order must match PBTCaseDetailTabs.
  const MODAL_FEATURE_PATH = 'page.hrcDashboard/modal.caseDetail';
  const detailsTabFeature = useFeature(`${MODAL_FEATURE_PATH}/tab.caseDetail.details`);
  const empDataTabFeature = useFeature(`${MODAL_FEATURE_PATH}/tab.caseDetail.empData`);
  const workflowTabFeature = useFeature(`${MODAL_FEATURE_PATH}/tab.caseDetail.workflow`);
  const auditTabFeature = useFeature(`${MODAL_FEATURE_PATH}/tab.caseDetail.audit`);
  const tabAccess = React.useMemo(
    () => [detailsTabFeature.enabled, empDataTabFeature.enabled, workflowTabFeature.enabled, auditTabFeature.enabled],
    [detailsTabFeature.enabled, empDataTabFeature.enabled, workflowTabFeature.enabled, auditTabFeature.enabled]
  );
  // Bug 16442069: while blocked on required Employee Data, the downstream tabs (Workflow Actions,
  // Audit Log) stay visible but are disabled so the HRC cannot navigate into steps that
  // cannot proceed until the data is resolved. Case Details and Employee Data remain interactive.
  const tabDisabled = React.useMemo(
    () => [false, false, isDownstreamLocked, isDownstreamLocked],
    [isDownstreamLocked]
  );
  const firstVisibleTab = tabAccess.findIndex((a, i) => a && !tabDisabled[i]);
  // A Date & Attestation Pending case defaults to the Workflow Actions tab, but that tab is
  // (correctly) disabled while the case-level completeness lookup is still in flight. Without this
  // guard the tab would briefly fall back to Case Details and then snap to Workflow Actions once the
  // lookup resolves — a visible flicker. While the lookup is pending and Workflow Actions is the
  // intended tab, keep it selected and show a spinner instead of flashing Case Details content.
  const resolvingDefaultWorkflowTab =
    empDataLookupPending &&
    WORKFLOW_ACTIONS_TAB_INDEX >= 0 &&
    activeTab === WORKFLOW_ACTIONS_TAB_INDEX &&
    tabAccess[WORKFLOW_ACTIONS_TAB_INDEX];
  // If the currently-selected tab is not visible for this role, not present, or disabled (e.g. a
  // downstream tab while blocked on employee data), fall back to the first visible & enabled tab so
  // content and the highlighted tab always agree.
  const effectiveActiveTab = resolvingDefaultWorkflowTab
    ? WORKFLOW_ACTIONS_TAB_INDEX
    : tabAccess[activeTab] && !tabDisabled[activeTab]
    ? activeTab
    : firstVisibleTab >= 0
    ? firstVisibleTab
    : 0;

  // Case Details fields are editable only for roles with Cases.Edit (HRC/HRCLeader/Admin).
  // Managers (Cases.ViewOwn only) get a read-only view — their sole action is attestation.
  const detailFieldsDisabled = isLocked || !canEditPerm;

  // Determine which actions are available (status-based validity AND role permission).
  const canPause =
    canPausePerm &&
    effectiveCase.status !== PBTCaseStatus.Complete &&
    effectiveCase.status !== PBTCaseStatus.Cancelled &&
    effectiveCase.status !== PBTCaseStatus.Paused &&
    !effectiveCase.termEmailSent;

  const canResume = canPausePerm && effectiveCase.status === PBTCaseStatus.Paused;

  // Cancel allowed for active or paused cases (AC #7) and after term email is sent (AC #11); not Complete or Cancelled
  const canCancel =
    canCancelPerm &&
    effectiveCase.status !== PBTCaseStatus.Cancelled &&
    (effectiveCase.status !== PBTCaseStatus.Complete || !!effectiveCase.termEmailSent);

  // Keep the AttestationPanel action aligned with the case-level bypass guards and feature visibility.
  const canBypass =
    bypassFeature.enabled &&
    !isLocked &&
    !isBypassed &&
    !isWorkflowBlocked &&
    effectiveCase.status !== PBTCaseStatus.Paused &&
    currentStep < 7 &&
    !bypassFeature.readOnly;

  // Reset form state when selected case changes
  React.useEffect(() => {
    setOutcome(selectedCase.outcome ?? '');
    setRehire(selectedCase.rehire ?? '');
    setEngModel(selectedCase.engModel ?? '');
    setBackupHrcAlias(selectedCase.backupHrc ?? '');
    setAssignedHrc(selectedCase.hrc ?? '');
    setDisplayedHrcName(selectedCase.hrcName ?? '—');
    setLastSavedOutcome(selectedCase.outcome ?? '');
    setLastSavedRehire(selectedCase.rehire ?? '');
    setLastSavedEngModel(selectedCase.engModel ?? '');
    setLastSavedBackupHrc(selectedCase.backupHrc ?? '');
    setLastSavedAssignedHrc(selectedCase.hrc ?? '');
    setIsTermDateDialogOpen(false);
    setActiveTab(getDefaultTabIndex(selectedCase.status));
    setSuccessMessage(null);
    setEmpDataBlocked(null);
    setCaseLevelBlocked(null);
    clearUpdatedCase();
  }, [selectedCase.id]);

  // Keep the local value in sync when a dashboard refresh updates the currently open case.
  // Local state is still updated immediately by TermDateDialog before that refresh completes.
  React.useEffect(() => {
    setTermConvDate(selectedCase.termConvDate);
  }, [selectedCase.id, selectedCase.termConvDate]);

  // Bug fix: when a live Retry Pull / Manual Override clears the last blocking employee-data field,
  // the backend promotes the case (e.g. Data Gathering -> Date & Attestation Pending). Refresh the
  // dashboard so the grid row and this panel's snapshot pick up the new status immediately instead
  // of only after a manual page reload. Fires only on a true -> false transition (an actual unblock),
  // never on the initial load of an already-unblocked case.
  const prevEmpDataBlockedRef = React.useRef<boolean | null>(null);
  // While the dashboard re-fetch triggered by the unblock is in flight the status badge would still
  // show the pre-promotion status with no feedback. Surface a lightweight "Updating…" state on the
  // badge so the user sees the case is being refreshed rather than a stale/unchanged status.
  const [isSyncingStatus, setIsSyncingStatus] = React.useState(false);
  React.useEffect(() => {
    if (prevEmpDataBlockedRef.current === true && empDataBlocked === false) {
      setIsSyncingStatus(true);
      onCaseReleased?.();
    }
    prevEmpDataBlockedRef.current = empDataBlocked;
  }, [empDataBlocked, onCaseReleased]);

  // Clear the syncing indicator as soon as the refreshed status arrives (the dashboard re-selects
  // the case from freshly fetched data, updating selectedCase.status).
  React.useEffect(() => {
    setIsSyncingStatus(false);
  }, [selectedCase.status]);

  // Safety net: if the refresh resolves without a status change (e.g. the case was already at its
  // final status), still clear the indicator so it can never get stuck spinning.
  React.useEffect(() => {
    if (!isSyncingStatus) return;
    const timer = setTimeout(() => setIsSyncingStatus(false), 6000);
    return () => clearTimeout(timer);
  }, [isSyncingStatus]);

  // Bug fix: surface the Workflow Actions tab whenever a case is (or becomes) Date & Attestation Pending.
  // Keyed on status rather than id so an in-place promotion (Data Gathering -> Date & Attestation Pending
  // after a successful Retry Pull) switches to the tab without waiting for the panel to be reopened.
  // Only forces the tab for Date & Attestation Pending so other transitions never yank the user's tab.
  React.useEffect(() => {
    if (selectedCase.status === PBTCaseStatus.AwaitingAttestation && WORKFLOW_ACTIONS_TAB_INDEX >= 0) {
      setActiveTab(WORKFLOW_ACTIONS_TAB_INDEX);
    }
  }, [selectedCase.status]);

  // Clear success message after 5 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Track in-flight save so we only update lastSaved* and show success after the async update succeeds
  const pendingSaveRef = React.useRef<{
    caseId: number;
    outcome: string;
    rehire: string;
    engModel: string;
    backupHrcAlias: string;
    assignedHrc: string;
    didReassignHrc: boolean;
  } | null>(null);
  const prevIsUpdatingRef = React.useRef<boolean>(isUpdating);

  React.useEffect(() => {
    const wasUpdating = prevIsUpdatingRef.current;
    const saveJustFinished = wasUpdating && !isUpdating && !!pendingSaveRef.current;

    if (saveJustFinished) {
      const saved = pendingSaveRef.current!;
      pendingSaveRef.current = null;

      if (!updateError) {
        // Always patch the module-level cache using the id that was actually saved,
        // not the currently-selected case id. Otherwise, if the user switched cases
        // while the save was in flight, we'd overwrite the newly selected case's cache
        // entry with the previous case's saved values.
        updateCaseInStore(saved.caseId, {
          outcome: saved.outcome,
          rehire: saved.rehire,
          engModel: saved.engModel as PBTEngagementModel,
          backupHrc: saved.backupHrcAlias,
          hrc: saved.assignedHrc,
        });

        // Only update local "last saved" state and show the success banner if we are
        // still viewing the same case the save was issued for.
        if (saved.caseId === selectedCase.id) {
          setLastSavedOutcome(saved.outcome);
          setLastSavedRehire(saved.rehire);
          setLastSavedEngModel(saved.engModel as PBTEngagementModel);
          setLastSavedBackupHrc(saved.backupHrcAlias);
          setLastSavedAssignedHrc(saved.assignedHrc);
          setSuccessMessage('Changes saved successfully!');

          // If the Assigned HRC was changed, we need a refresh to get the new `hrcName`
          // (the local cache patch only updates the alias).
          if (saved.didReassignHrc) {
            onCaseUpdated?.();
          }
        }
      }
    }

    prevIsUpdatingRef.current = isUpdating;
  }, [isUpdating, updateError, selectedCase.id, onCaseUpdated]);

  const handleSave = () => {
    if (!hasChanges || isLocked) return;

    // Determine impacts for confirmation (only check fields that are ACTUALLY changing in this save)
    const impacts: string[] = [];
    
    if (rehire !== lastSavedRehire) {
      impacts.push('Term memo version will be re-selected and regenerated if already generated');
    }
    
    if (engModel !== lastSavedEngModel) {
      const wasHrcLed = lastSavedEngModel === PBTEngagementModel.HRCLed;
      const isNowHrcLed = engModel === PBTEngagementModel.HRCLed;
      
      if (isNowHrcLed && !wasHrcLed) {
        impacts.push('Manager emails will be suppressed; manager actions will route to HRC');
      } else if (!isNowHrcLed && wasHrcLed) {
        impacts.push('Manager Kickoff Email will be triggered if not already sent; manager actions will route to manager');
      } else {
        impacts.push('Manager engagement workflow will be adjusted');
      }
    }

    // Show confirmation if there are downstream impacts
    if (impacts.length > 0) {
      setConfirmImpacts(impacts);
      setConfirmDialogVisible(true);
    } else {
      performSave();
    }
  };

  const performSave = () => {
    const request: IUpdatePbtCaseRequest = {};
    
    if (outcome !== lastSavedOutcome) {
      request.CrmOutcome = outcome;
    }
    if (rehire !== lastSavedRehire) {
      request.RehireEligibility = rehire;
    }
    if (engModel !== lastSavedEngModel) {
      request.EngagementModel = engModel;
    }
    if (backupHrcAlias !== lastSavedBackupHrc) {
      request.BackupHrcAlias = backupHrcAlias;
    }
    if (assignedHrc !== lastSavedAssignedHrc) {
      request.HrcAlias = assignedHrc;
    }

    pendingSaveRef.current = {
      caseId: selectedCase.id,
      outcome,
      rehire,
      engModel,
      backupHrcAlias,
      assignedHrc,
      didReassignHrc: assignedHrc !== lastSavedAssignedHrc,
    };
    setSuccessMessage(null);

    dispatch(updateCaseRequest(selectedCase.id, request));
    setConfirmDialogVisible(false);
  };

  // HRC Bypass handler
  const handleBypassConfirm = React.useCallback(async () => {
    const success = await bypassManager(selectedCase.id);
    if (success) {
      setShowBypassDialog(false);
      onCaseUpdated?.();
    }
  }, [bypassManager, selectedCase.id, onCaseUpdated]);

  // Sync engModel state when it changes (after bypass) without resetting tab
  React.useEffect(() => {
    const nextEngModel = selectedCase.engModel ?? '';
    setEngModel(nextEngModel);
    setLastSavedEngModel(nextEngModel as PBTEngagementModel);
  }, [selectedCase.engModel]);

  // Helper to determine if term date button should be disabled/hidden
  const isTermDateButtonDisabled = () => {
    const blockedStatuses: PBTCaseStatus[] = [
      PBTCaseStatus.Complete,
      PBTCaseStatus.AwaitingHrcApproval,
      PBTCaseStatus.Paused,
      PBTCaseStatus.Cancelled,
    ];
    return hasPendingAttestationSuccess || isTermDateSelectionBlocked || setTermDateFeature.readOnly || blockedStatuses.includes(effectiveCase.status);
  };

  const shouldShowTermDateButton = setTermDateFeature.enabled && !isTermDateButtonDisabled();

  return (
    <div className={caseDetailStyles.container}>
      {/* Locked State Banner */}
      {isLocked && (
        <div style={{ 
          backgroundColor: '#fef6f6', 
          border: '1px solid #a4262c', 
          borderRadius: '4px', 
          padding: '12px 16px', 
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Icon iconName="Lock" style={{ color: '#a4262c', fontSize: 16 }} />
          <Text style={{ color: '#a4262c', fontWeight: 600 }}>
            {lockMessage}
          </Text>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </MessageBar>
      )}

      {/* Error Message */}
      {updateError && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => dispatch({ type: 'PBT/UPDATE_CASE_ERROR', errorMessage: null })}>
          {updateError}
        </MessageBar>
      )}

      {/* Action result banner */}
      {actionResult && (
        <MessageBar
          messageBarType={actionResult.success ? MessageBarType.success : MessageBarType.error}
          onDismiss={clearActionResult}
          styles={{ root: { marginBottom: 8 } }}
        >
          {actionResult.message}
          {actionResult.warnings && actionResult.warnings.length > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
              {actionResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </MessageBar>
      )}

      {/* Info Card */}
      {!isManager && reviewedIndicators.length > 0 && (
        <MessageBar messageBarType={MessageBarType.success} styles={{ root: { marginBottom: 8 } }}>
          <span>{`${reviewedIndicators.join(', ')} reviewed by HRC. The indicators remain visible for reference and no longer block this case.`}</span>
        </MessageBar>
      )}

      {/* Term-email delivery failure (P0-3 / US 15949981): the case status can read "Complete" because
          it is set when the email is queued, but the async EmailProcessor later failed to deliver it.
          Surface it so the HRC knows follow-up is required and the offboarding is being held. */}
      {effectiveCase.emailFailed && !emailFailedCleared && (
        <div style={{
          backgroundColor: '#fef6f6',
          border: '1px solid #a4262c',
          borderRadius: '4px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon iconName="MailAlert" style={{ color: '#a4262c', fontSize: 16 }} />
            <Text style={{ color: '#a4262c', fontWeight: 600 }}>
              Termination email delivery failed. The recipient(s) did not receive the email, so offboarding is on hold. Please review and re-send the termination email.
            </Text>
          </div>
          {canEditPerm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PrimaryButton
                text={isRetryingEmail ? 'Re-sending…' : 'Re-send termination email'}
                iconProps={{ iconName: 'Send' }}
                onClick={handleRetryTermEmail}
                disabled={isRetryingEmail}
              />
              {isRetryingEmail && <Spinner size={SpinnerSize.small} />}
            </div>
          )}
        </div>
      )}

      {retryEmailMessage && (
        <MessageBar
          messageBarType={retryEmailMessage.type}
          onDismiss={() => setRetryEmailMessage(null)}
          styles={{ root: { marginBottom: 8 } }}
        >
          {retryEmailMessage.text}
        </MessageBar>
      )}

      <div className={caseDetailStyles.infoCard}>
        {/* Row 1 */}
        <Text className={caseDetailStyles.infoLabel}>Employee</Text>
        <div className={caseDetailStyles.infoValue}>{selectedCase.empName}</div>
        <Text className={caseDetailStyles.infoLabel}>CRM Case</Text>
        <div className={caseDetailStyles.infoValue} style={{ fontFamily: 'monospace' }}>{selectedCase.crmCase}</div>
        <span className={caseDetailStyles.statusBadge}>
          {isSyncingStatus ? (
            <Spinner size={SpinnerSize.xSmall} label="Updating..." labelPosition="right" />
          ) : (
            effectiveCase.status
          )}
        </span>
        {/* Row 2 */}
        <Text className={caseDetailStyles.infoLabel}>PERN</Text>
        <div className={caseDetailStyles.infoValue}>{selectedCase.pern}</div>
        {!isManager && (
          <>
            <Text className={caseDetailStyles.infoLabel}>Engagement</Text>
            <div className={caseDetailStyles.infoValue}>{selectedCase.engModel}</div>
          </>
        )}
        {/* Row 3 */}
        <Text className={caseDetailStyles.infoLabel}>Manager</Text>
        <div className={caseDetailStyles.infoValue}>
          {selectedCase.manager}
          {selectedCase.isManagerChanged && (
            <Icon
              iconName={effectiveCase.isManagerChangeReviewed ? 'CompletedSolid' : 'Warning'}
              title={effectiveCase.isManagerChangeReviewed
                ? 'Manager change reviewed by HRC'
                : 'Manager changed in CRM after this case was created'}
              aria-label={effectiveCase.isManagerChangeReviewed
                ? 'Manager change reviewed by HRC'
                : 'Manager changed in CRM after this case was created'}
              style={{
                marginLeft: 6,
                color: effectiveCase.isManagerChangeReviewed ? '#107c10' : '#d29200',
                verticalAlign: 'middle',
                cursor: 'default',
              }}
            />
          )}
        </div>
        <Text className={caseDetailStyles.infoLabel}>HRC Name</Text>
        <div className={caseDetailStyles.infoValue}>{displayedHrcName}</div>
      </div>

      {/* Workflow Stepper */}
      <div className={caseDetailStyles.stepperContainer}>
        <div className={caseDetailStyles.stepperLine}>
          {displaySteps.map((step, idx) => {
            const stepNum = idx + 1;
            // Bug 16442169: when the case is blocked on required Employee Data, the Data Gathering
            // node must signal the failure with an X (error) instead of a completion check mark.
            const isBlockedStep = isBlockedOnData && step === 'Data Gathering';
            let circleClass = caseDetailStyles.stepCircleInactive;
            if (isBlockedStep) circleClass = caseDetailStyles.stepCircleBlocked;
            else if (stepNum < displayCurrentStep) circleClass = caseDetailStyles.stepCircle;
            else if (stepNum === displayCurrentStep) circleClass = caseDetailStyles.stepCircleActive;

            return (
              <div key={step} className={caseDetailStyles.stepItem}>
                <div className={circleClass}>
                  {isBlockedStep ? (
                    <Icon iconName="Cancel" aria-label="Blocked" />
                  ) : stepNum < displayCurrentStep ? (
                    <Icon iconName="CheckMark" />
                  ) : (
                    stepNum
                  )}
                </div>
                <span className={caseDetailStyles.stepLabel}>{step}</span>
                {idx < displaySteps.length - 1 && (
                  <div className={stepNum < displayCurrentStep ? caseDetailStyles.stepConnector : caseDetailStyles.stepConnectorInactive} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isWorkflowBlocked && (
        <div className={caseDetailStyles.workflowBlockedBanner} role="alert">
          <Icon iconName="Warning" aria-hidden="true" />
          <span>{workflowBlockedMessage}</span>
        </div>
      )}

      {/* Tabs */}
      <div className={caseDetailStyles.tabsContainer} role="tablist" aria-label="Case detail tabs">
        {TABS.map((tab, idx) =>
          tabAccess[idx] ? (
            <button
              key={tab}
              className={
                idx === effectiveActiveTab
                  ? caseDetailStyles.tabActive
                  : tabDisabled[idx]
                  ? caseDetailStyles.tabDisabled
                  : caseDetailStyles.tab
              }
              onClick={() => setActiveTab(idx)}
              type="button"
              role="tab"
              disabled={tabDisabled[idx]}
              aria-selected={idx === effectiveActiveTab}
              aria-disabled={tabDisabled[idx]}
              title={tabDisabled[idx] ? 'Resolve the required Employee Data to access this tab' : undefined}
              aria-controls={`tabpanel-${idx}`}
              id={`tab-${idx}`}
            >
              {tab}
            </button>
          ) : null
        )}
      </div>

      {/* Tab Content */}
      <div className={caseDetailStyles.tabContent} role="tabpanel" id={`tabpanel-${effectiveActiveTab}`} aria-labelledby={`tab-${effectiveActiveTab}`}>
        {resolvingDefaultWorkflowTab && (
          <Spinner size={SpinnerSize.medium} label="Loading case..." />
        )}
        {!resolvingDefaultWorkflowTab && effectiveActiveTab === 0 && tabAccess[0] && (
          <>
            <div className={caseDetailStyles.formGrid}>
              <div>
                <Text className={caseDetailStyles.formLabel}>CRM Case Outcome</Text>
                <Dropdown
                  placeholder="— Select Outcome —"
                  options={outcomeOptions}
                  selectedKey={outcome}
                  onChange={(_, opt) => setOutcome(opt?.key as string ?? '')}
                  ariaLabel="CRM Case Outcome"
                  disabled={detailFieldsDisabled}
                />
              </div>
              <div>
                <Text className={caseDetailStyles.formLabel}>Rehire Eligibility</Text>
                <Dropdown
                  placeholder="— Select Rehire —"
                  options={rehireOptions}
                  selectedKey={rehire}
                  onChange={(_, opt) => setRehire(opt?.key as string ?? '')}
                  ariaLabel="Rehire Eligibility"
                  disabled={detailFieldsDisabled}
                />
              </div>
              {!isManager && (
                <div>
                  <Text className={caseDetailStyles.formLabel}>Manager Engagement Model</Text>
                  <Dropdown
                    placeholder="— Select Model —"
                    options={engModelOptions}
                    selectedKey={engModel}
                    onChange={(_, opt) => setEngModel(opt?.key as string ?? '')}
                    ariaLabel="Manager Engagement Model"
                    disabled={detailFieldsDisabled || !!selectedCase.geoRestricted}
                  />
                  <p className={caseDetailStyles.editableNote}>
                    {selectedCase.geoRestricted
                      ? 'Locked to HRC-led — this case is in a restricted location (manager bypassed).'
                      : 'Editable until term email is sent.'}
                  </p>
                </div>
              )}
              <div>
                <Text className={caseDetailStyles.formLabel}>Backup HRC Alias</Text>
                <TextField
                  placeholder="Enter backup HRC alias"
                  value={backupHrcAlias}
                  onChange={(_, val) => setBackupHrcAlias(val ?? '')}
                  ariaLabel="Backup HRC Alias"
                  disabled={detailFieldsDisabled}
                />
              </div>
              <div>
                <Text className={caseDetailStyles.formLabel}>Assigned HRC</Text>
                {selectedCase.geoRestricted && !isLocked ? (
                  <>
                    <TextField
                      placeholder="Enter new executor alias"
                      value={assignedHrc}
                      onChange={(_, val) => setAssignedHrc(val ?? '')}
                      ariaLabel="Assigned HRC"
                    />
                    <p className={caseDetailStyles.editableNote}>
                      Reassign execution to another authorized, non-restricted individual. The restricted manager cannot be assigned.
                    </p>
                  </>
                ) : (
                  <div 
                    className={caseDetailStyles.formValue}
                    style={isLocked ? { color: '#a0a0a0', opacity: 0.6 } : {}}
                  >
                    {selectedCase.hrc || '—'}
                  </div>
                )}
              </div>
            </div>
            {!isLocked && (
              <div className={caseDetailStyles.footerActions}>
                {canEditPerm && (
                  <button 
                    type="button" 
                    className={caseDetailStyles.saveButton}
                    onClick={() => {
                      if (hasChanges && !isUpdating) handleSave();
                    }}
                    aria-disabled={!hasChanges || isUpdating}
                  >
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
                <button type="button" className={caseDetailStyles.cancelButton} onClick={onClose}>Cancel</button>
              </div>
            )}
            {/* Pause / Resume / Cancel Case Actions */}
            {(canPause || canResume || canCancel) && (
              <div className={caseDetailStyles.footerActions}>
                {canPause && (
                  <button
                    type="button"
                    className={caseDetailStyles.pauseButton}
                    onClick={() => openPauseDialog(selectedCase)}
                  >
                    <Icon iconName="Pause" styles={{ root: { marginRight: 6 } }} />
                    Pause Case
                  </button>
                )}
                {canResume && (
                  <button
                    type="button"
                    className={caseDetailStyles.resumeButton}
                    onClick={() => openResumeDialog(selectedCase)}
                  >
                    <Icon iconName="Play" styles={{ root: { marginRight: 6 } }} />
                    Resume Case
                  </button>
                )}
                {canCancel && (
                  <button
                    type="button"
                    className={caseDetailStyles.cancelCaseButton}
                    onClick={() => openCancelDialog(selectedCase)}
                  >
                    <Icon iconName="Cancel" styles={{ root: { marginRight: 6 } }} />
                    Cancel Case
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {effectiveActiveTab === 1 && tabAccess[1] && (
          <EmployeeDataTab
            personnelNumber={selectedCase.pern}
            pbtCaseId={selectedCase.id}
            gerWitCheckStatus={effectiveCase.gerWitCheckStatus}
            onBlockedChange={setEmpDataBlocked}
          />
        )}
        {!resolvingDefaultWorkflowTab && effectiveActiveTab === 2 && tabAccess[2] && (
          <>
            <div className={caseDetailStyles.currentStepTitle}>
              Current Step: {displaySteps[displayCurrentStep - 1] || 'Unknown'}
            </div>
            <div className={caseDetailStyles.currentStepSubtitle}>
              Step {displayCurrentStep} of {displaySteps.length}
            </div>

            {/* Manager Kickoff Email Card */}
            {kickoffEmailFeature.enabled && (
            <div className={caseDetailStyles.workflowCard}>
              <div className={caseDetailStyles.workflowCardHeader}>
                <div className={caseDetailStyles.workflowCardTitle}>
                  <Icon iconName="Mail" style={{ marginRight: 8, color: '#0078d4' }} />
                  Manager Kickoff Email
                </div>
                {selectedCase.engModel === PBTEngagementModel.HRCLed ? (
                  <span className={caseDetailStyles.skippedBadge}>
                    <Icon iconName="Forward" className={caseDetailStyles.badgeIcon} />
                    Skipped
                  </span>
                ) : selectedCase.kickoffEmailSent ? (
                  <span className={caseDetailStyles.sentBadge}>
                    <Icon iconName="CheckboxComposite" className={caseDetailStyles.badgeIcon} />
                    Sent
                  </span>
                ) : (
                  <span className={caseDetailStyles.pendingBadge}>
                    <Icon iconName="Clock" className={caseDetailStyles.badgeIcon} />
                    Pending
                  </span>
                )}
              </div>
              {selectedCase.engModel === PBTEngagementModel.HRCLed ? (
                <div className={caseDetailStyles.workflowFieldValue} style={{ color: '#6b7280', fontWeight: 400, fontStyle: 'italic' }}>
                  Manager engagement is not required for this case. The HRC is managing the process directly.
                </div>
              ) : (
                <>
                  <div className={caseDetailStyles.workflowField}>
                    <span className={caseDetailStyles.workflowFieldLabel}>Sent Date</span>
                    <span className={caseDetailStyles.workflowFieldValue}>
                      {selectedCase.kickoffEmailSentAt
                        ? new Date(selectedCase.kickoffEmailSentAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                    </span>
                  </div>
                  <div className={caseDetailStyles.workflowField}>
                    <span className={caseDetailStyles.workflowFieldLabel}>Sent To</span>
                    <span className={caseDetailStyles.workflowFieldValue}>{selectedCase.manager || '—'}</span>
                  </div>
                </>
              )}
            </div>
            )}

            {/* Manager Attestation Card */}
            <div className={caseDetailStyles.workflowCard}>
              <div className={caseDetailStyles.workflowCardTitle}>
                {isBypassed
                  ? 'HRC Actions (Manager Bypassed)'
                  : termConvDate
                  ? 'Manager Attestation'
                  : 'Select Conversation Date'}
              </div>
              <div className={caseDetailStyles.workflowField}>
                <span className={caseDetailStyles.workflowFieldLabel}>{actionActor.label}</span>
                <span className={caseDetailStyles.workflowFieldValue}>{actionActor.value}</span>
              </div>
              <div className={caseDetailStyles.workflowField}>
                <span className={caseDetailStyles.workflowFieldLabel}>Term Conv Date</span>
                <span className={caseDetailStyles.workflowFieldValue}>
                  {termConvDate && (() => {
                    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(termConvDate);
                    if (match) {
                      const [, y, m, d] = match;
                      return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
                    }
                    return new Date(termConvDate).toLocaleDateString();
                  })()}
                  {shouldShowTermDateButton ? (
                    <DefaultButton
                      text={termConvDate ? 'Change Date' : 'Select Date'}
                      iconProps={{ iconName: 'Calendar' }}
                      onClick={() => setIsTermDateDialogOpen(true)}
                      disabled={isTermDateButtonDisabled()}
                      styles={{ root: { marginLeft: termConvDate ? 12 : 0 } }}
                    />
                  ) : !termConvDate ? <>—</> : null}
                </span>
              </div>
              <div className={caseDetailStyles.workflowField}>
                <span className={caseDetailStyles.workflowFieldLabel}>Days Waiting</span>
                <span className={caseDetailStyles.workflowFieldValue}>{selectedCase.daysInStatus} days</span>
              </div>
              {isBypassed && (
                <div className={caseDetailStyles.bypassBanner}>
                  <Icon iconName="Info" styles={{ root: { marginRight: 8 } }} />
                  Manager notifications are suppressed. All actions are routed to HRC.
                </div>
              )}
            </div>

            <AttestationPanel
              selectedCase={effectiveCase}
              blocked={isWorkflowBlocked}
              onSubmitted={() => setHasPendingAttestationSuccess(true)}
              onPauseCase={() => openPauseDialog(effectiveCase)}
              onResumeCase={() => openResumeDialog(effectiveCase)}
              onCancelCase={() => openCancelDialog(effectiveCase)}
              onBypassManager={() => setShowBypassDialog(true)}
              canPause={canPause}
              canResume={canResume}
              canCancel={canCancel}
              canBypass={canBypass}
            />

            {/* Send Reminder Section */}
            {remindersFeature.enabled && (
            <div className={caseDetailStyles.workflowCard}>
              <div className={caseDetailStyles.workflowCardTitle}>
                Reminder Actions
              </div>
              <div className={caseDetailStyles.workflowField}>
                <span className={caseDetailStyles.workflowFieldLabel}>Reminders Sent</span>
                <span className={caseDetailStyles.workflowFieldValue}>{localReminderCount}</span>
              </div>
              {localLastSentAt && (
                <div className={caseDetailStyles.workflowField}>
                  <span className={caseDetailStyles.workflowFieldLabel}>Last Sent</span>
                  <span className={caseDetailStyles.workflowFieldValue}>{new Date(localLastSentAt).toLocaleDateString()}</span>
                </div>
              )}
              {!isBypassed && selectedCase.managerNonResponsive && (
                <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 8 } }}>
                  Manager flagged as Non-Responsive. No more reminders will be sent.
                </MessageBar>
              )}
              {reminderMessage && (
                <MessageBar
                  messageBarType={reminderMessage.type}
                  onDismiss={() => setReminderMessage(null)}
                  styles={{ root: { marginBottom: 8 } }}
                >
                  {reminderMessage.text}
                </MessageBar>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                {isBypassed ? (
                  <MessageBar messageBarType={MessageBarType.info}>
                    {PBTMessages.ReminderManagerBypassed}
                  </MessageBar>
                ) : (
                  <>
                    <PrimaryButton
                      iconProps={{ iconName: 'Mail' }}
                      text="Send Reminder"
                      onClick={handleSendReminder}
                      disabled={!canSendReminder || isSendingReminder || isWorkflowBlocked}
                    />
                    {isSendingReminder && <Spinner size={SpinnerSize.small} />}
                  </>
                )}
              </div>
              {!isBypassed && !canSendReminder && selectedCase.kickoffEmailSent !== true && (
                <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 8 } }}>
                  {PBTMessages.ReminderKickoffNotSent}
                </MessageBar>
              )}
              {!isBypassed && !canSendReminder && selectedCase.termConvDate != null && (
                <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 8 } }}>
                  {PBTMessages.ReminderNotNeeded}
                </MessageBar>
              )}
            </div>
            )}

            {/* Bypass Confirmation Dialog */}
            <Dialog
              hidden={!showBypassDialog}
              onDismiss={() => setShowBypassDialog(false)}
              dialogContentProps={{
                type: DialogType.normal,
                title: 'Bypass Manager',
                subText: 'Are you sure you want to bypass the manager? This will route all manager actions to the HRC and suppress manager email notifications.',
              }}
            >
              {bypassError && (
                <Text styles={{ root: { color: '#a4262c', display: 'block', marginBottom: 8 } }}>
                  {bypassError}
                </Text>
              )}
              <DialogFooter>
                <PrimaryButton
                  text={isSubmitting ? 'Processing...' : 'Confirm Bypass'}
                  onClick={handleBypassConfirm}
                  disabled={isSubmitting}
                />
                <DefaultButton 
                  text="Cancel" 
                  onClick={() => setShowBypassDialog(false)} 
                  disabled={isSubmitting} 
                />
              </DialogFooter>
            </Dialog>

            {isAdmin && (isAwaitingHrcApproval || proceedDone || proceedProcessing) && (
              <div className={caseDetailStyles.hrcGateAction}>
                {proceedDone ? (
                  <Text role="status" aria-live="polite" styles={{ root: { color: '#0b6a0b', fontWeight: 600 } }}>
                    <Icon iconName="CheckMark" styles={{ root: { marginRight: 6 } }} />
                    {PBTMessages.ProceedSuccess}
                  </Text>
                ) : proceedProcessing ? (
                  <Text role="status" aria-live="polite" styles={{ root: { color: '#3730a3', fontWeight: 600 } }}>
                    <Icon iconName="Sync" styles={{ root: { marginRight: 6 } }} />
                    {proceedState === 'Preparing' ? PBTMessages.ProceedPreparing : PBTMessages.ProceedProcessing}
                  </Text>
                ) : (
                  <>
                    <Text styles={{ root: { color: '#3730a3' } }}>
                      <Icon iconName="ReviewSolid" styles={{ root: { marginRight: 6 } }} />
                      This case is awaiting HRC validation. Review the data, then release it to continue.
                    </Text>
                    <button
                      type="button"
                      className={caseDetailStyles.proceedButton}
                      onClick={() => setConfirmVisible(true)}
                      disabled={proceeding || proceedProcessing || isWorkflowBlocked || !canProceedPerm}
                      style={!canProceedPerm ? { display: 'none' } : undefined}
                    >
                      Proceed
                    </button>
                    {proceedError && (
                      <Text role="alert" aria-live="assertive" styles={{ root: { color: '#a4262c' } }}>{proceedError}</Text>
                    )}
                  </>
                )}
                <Dialog
                  hidden={!confirmVisible}
                  onDismiss={() => !proceeding && setConfirmVisible(false)}
                  dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Proceed with HRC validation?',
                    subText: `This will release case ${selectedCase.crmCase} from the HRC validation gate and resume the held downstream actions (term email, etc.). This cannot be undone.`,
                  }}
                  modalProps={{ isBlocking: true }}
                >
                  <DialogFooter>
                    <PrimaryButton
                      onClick={onProceed}
                      disabled={proceeding || isWorkflowBlocked}
                      text={proceeding ? 'Proceeding…' : 'Proceed'}
                      ariaLabel="Confirm proceed with HRC validation"
                    />
                    <DefaultButton
                      onClick={() => setConfirmVisible(false)}
                      disabled={proceeding}
                      text="Cancel"
                      ariaLabel="Cancel proceed"
                    />
                  </DialogFooter>
                </Dialog>
              </div>
            )}

            {!isAdmin && isAwaitingHrcApproval && (
              <div className={caseDetailStyles.hrcGateAction}>
                <Text role="status" aria-live="polite" styles={{ root: { color: '#3730a3' } }}>
                  <Icon iconName="ReviewSolid" styles={{ root: { marginRight: 6 } }} />
                  This case is awaiting release by an administrator.
                </Text>
              </div>
            )}
          </>
        )}
        {effectiveActiveTab === 3 && tabAccess[3] && (
          <AuditLog caseId={selectedCase.id} />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        visible={confirmDialogVisible}
        impacts={confirmImpacts}
        onConfirm={performSave}
        onCancel={() => setConfirmDialogVisible(false)}
      />

      {/* Pause / Cancel Dialogs */}
      <PauseCaseDialog
        isOpen={pauseDialogOpen}
        caseName={targetCase?.empName || selectedCase.empName}
        onConfirm={handlePauseConfirm}
        onDismiss={closePauseDialog}
      />
      <CancelCaseDialog
        isOpen={cancelDialogOpen}
        caseName={targetCase?.empName || selectedCase.empName}
        termEmailSent={selectedCase.termEmailSent}
        onConfirm={handleCancelConfirm}
        onDismiss={closeCancelDialog}
      />
      <ResumeCaseDialog
        isOpen={resumeDialogOpen}
        caseName={targetCase?.empName || selectedCase.empName}
        onConfirm={handleResumeConfirm}
        onDismiss={closeResumeDialog}
      />
      <TermDateDialog
        isOpen={isTermDateDialogOpen}
        selectedCase={{ ...selectedCase, termConvDate }}
        onDismiss={() => setIsTermDateDialogOpen(false)}
        onSuccess={(newDate) => {
          setTermConvDate(newDate);
          updateCaseInStore(selectedCase.id, { termConvDate: newDate });
        }}
      />
    </div>
  );
};
